// frontend/modules/loan-disbursement/components/dashboard/PendingDisbursements.tsx
import React from 'react';
import { Card, List, Button, Tag, Typography, Space, Spin, Empty } from 'antd';
import { DollarOutlined, EyeOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useLoanDisbursement } from '../../hooks/useLoanDisbursement';
import { formatCurrency, formatDate } from '@/shared/utils/formatters';
import { AppapproDTO } from '@/shared/api/model';
import {useTheme} from "@/components/common/ThemeProvider";

const { Text, Title } = Typography;

export const PendingDisbursements: React.FC = () => {
  const { dark } = useTheme();
  const navigate = useNavigate();
  const { 
    approvedLoans, 
    isLoading, 
    selectLoanForDisbursement,
    viewLoanDetails 
  } = useLoanDisbursement();

  // Filter for approved but not disbursed loans (top 5 for dashboard)
  const pendingDisbursements = approvedLoans
    .filter(loan => loan.approved && !loan.disbursed)
    .slice(0, 5);

  const handleViewAll = () => {
    navigate('/loans/disbursement');
  };

  const handleDisburse = (loan: AppapproDTO) => {
    selectLoanForDisbursement(loan);
  };

  const handleViewDetails = (loan: AppapproDTO) => {
    viewLoanDetails(loan);
  };

  if (isLoading) {
    return (
      <Card 
        title={
          <Space>
            <ClockCircleOutlined />
            <span>Pending Disbursements</span>
          </Space>
        }
        className={dark ? 'bg-gray-800 border-gray-700' : 'bg-white'}
      >
        <div className="flex justify-center items-center py-8">
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  return (
    <Card
      title={
        <Space>
          <ClockCircleOutlined />
          <span>Pending Disbursements</span>
          <Tag color="processing">{pendingDisbursements.length}</Tag>
        </Space>
      }
      extra={
        pendingDisbursements.length > 0 && (
          <Button type="link" onClick={handleViewAll}>
            View All
          </Button>
        )
      }
      className={dark ? 'bg-gray-800 border-gray-700' : 'bg-white'}
    >
      {pendingDisbursements.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No pending disbursements"
          className="py-4"
        />
      ) : (
        <List
          dataSource={pendingDisbursements}
          renderItem={(loan) => (
            <List.Item
              actions={[
                <Button
                  key="view"
                  type="text"
                  icon={<EyeOutlined />}
                  size="small"
                  onClick={() => handleViewDetails(loan)}
                >
                  View
                </Button>,
                <Button
                  key="disburse"
                  type="primary"
                  icon={<DollarOutlined />}
                  size="small"
                  onClick={() => handleDisburse(loan)}
                >
                  Disburse
                </Button>
              ]}
            >
              <List.Item.Meta
                title={
                  <Space>
                    <Text strong>{loan.customerName}</Text>
                    <Tag color="blue">
                      {loan.glNo}
                    </Tag>
                  </Space>
                }
                description={
                  <div>
                    <div className="text-sm">
                      <Text type="secondary">Product: </Text>
                      <Text>{loan.productName}</Text>
                    </div>
                    <div className="text-sm">
                      <Text type="secondary">Amount: </Text>
                      <Text strong className="text-green-600">
                        {formatCurrency(loan.loanAmt)}
                      </Text>
                    </div>
                    <div className="text-sm">
                      <Text type="secondary">Approved: </Text>
                      <Text>{formatDate(loan.approDate)}</Text>
                    </div>
                  </div>
                }
              />
            </List.Item>
          )}
        />
      )}
    </Card>
  );
};
